import React from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, ChipProps } from '@mui/material';
import { Warning, FavoriteOutlined } from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// TODO: Replace with generated types once schema is updated
export enum GqlScoreType {
  RISK = 'RISK',
  WELLNESS = 'WELLNESS'
}

interface ScoreTypeBadgeProps {
  scoreType: GqlScoreType;
  size?: 'small' | 'medium'; // MUI Chip only supports small and medium
  variant?: 'filled' | 'outlined';
  showIcon?: boolean;
  showLabel?: boolean;
}

const StyledChip = styled(Chip)<{ scoreType: GqlScoreType }>(({ theme, scoreType }) => ({
  fontWeight: 500,
  ...(scoreType === GqlScoreType.RISK ? {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.dark,
    '& .MuiChip-icon': {
      color: theme.palette.warning.main,
    },
    '&.MuiChip-outlined': {
      borderColor: theme.palette.warning.main,
      backgroundColor: 'transparent',
    }
  } : {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.dark,
    '& .MuiChip-icon': {
      color: theme.palette.success.main,
    },
    '&.MuiChip-outlined': {
      borderColor: theme.palette.success.main,
      backgroundColor: 'transparent',
    }
  })
}));

const ScoreTypeBadge: React.FC<ScoreTypeBadgeProps> = ({
  scoreType,
  size = 'medium' as const,
  variant = 'filled' as const,
  showIcon = true,
  showLabel = true
}) => {
  const { t } = useTranslation();

  const getIcon = () => {
    if (!showIcon) return undefined;
    
    return scoreType === GqlScoreType.RISK 
      ? <Warning fontSize="small" />
      : <FavoriteOutlined fontSize="small" />;
  };

  const getLabel = () => {
    if (!showLabel) return '';
    
    return scoreType === GqlScoreType.RISK
      ? t('scoreType.risk', 'Risk')
      : t('scoreType.wellness', 'Wellness');
  };

  // If both icon and label are hidden, just show the icon
  if (!showIcon && !showLabel) {
    return (
      <StyledChip
        scoreType={scoreType}
        icon={getIcon()}
        size={size}
        variant={variant}
      />
    );
  }

  return (
    <StyledChip
      scoreType={scoreType}
      icon={getIcon()}
      label={getLabel()}
      size={size}
      variant={variant}
    />
  );
};

export default ScoreTypeBadge;